import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateSetting } from '../../../store/settingsSlice';
import { DEFAULT_BLOCK_TYPES } from '../../../hooks/useTimeblock';
import { v4 as uuidv4 } from 'uuid';

const TimeBlockSettings = () => {
    const dispatch = useDispatch();
    const settings = useSelector(state => state.settings.settings);
    const blockTypes = settings?.timeblockTypes || DEFAULT_BLOCK_TYPES;
    const templates = settings?.timeblockTemplates || [];

    const [newLabel, setNewLabel] = useState('');
    const [newColor, setNewColor] = useState('#6366f1');
    const [editingId, setEditingId] = useState(null);
    const [editLabel, setEditLabel] = useState('');
    const [editColor, setEditColor] = useState('');

    const [tplRenamingId, setTplRenamingId] = useState(null);
    const [tplRenameValue, setTplRenameValue] = useState('');
    const [tplDeleteConfirmId, setTplDeleteConfirmId] = useState(null);

    const save = (updated) => {
        dispatch(updateSetting({ key: 'timeblockTypes', value: updated }));
    };

    const saveTemplates = (updated) => {
        dispatch(updateSetting({ key: 'timeblockTemplates', value: updated }));
    };

    const handleTplRenameStart = (t) => {
        setTplRenamingId(t.id);
        setTplRenameValue(t.name);
        setTplDeleteConfirmId(null);
    };

    const handleTplRenameSave = () => {
        if (!tplRenameValue.trim()) return;
        saveTemplates(templates.map(t => t.id === tplRenamingId ? { ...t, name: tplRenameValue.trim() } : t));
        setTplRenamingId(null);
    };

    const handleTplDelete = (id) => {
        saveTemplates(templates.filter(t => t.id !== id));
        setTplDeleteConfirmId(null);
    };

    const getBlockType = (id) => blockTypes.find(bt => bt.id === id) || { label: id, color: '#888' };

    const handleAdd = () => {
        if (!newLabel.trim()) return;
        const updated = [
            ...blockTypes,
            { id: uuidv4(), label: newLabel.trim(), color: newColor }
        ];
        save(updated);
        setNewLabel('');
        setNewColor('#6366f1');
    };

    const handleStartEdit = (bt) => {
        setEditingId(bt.id);
        setEditLabel(bt.label);
        setEditColor(bt.color);
    };

    const handleSaveEdit = () => {
        if (!editLabel.trim()) return;
        const updated = blockTypes.map(bt =>
            bt.id === editingId ? { ...bt, label: editLabel.trim(), color: editColor } : bt
        );
        save(updated);
        setEditingId(null);
    };

    const handleDelete = (id) => {
        save(blockTypes.filter(bt => bt.id !== id));
    };

    return (
        <div className="p-4 space-y-6">
            <div>
                <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wide mb-3">
                    Block Types
                </h3>
                <div className="space-y-2">
                    {blockTypes.map(bt => (
                        <div key={bt.id} className="flex items-center gap-3 py-2 px-3 rounded-lg bg-[var(--surface-bg-lighter)]">
                            {editingId === bt.id ? (
                                <>
                                    <input
                                        type="color"
                                        value={editColor}
                                        onChange={e => setEditColor(e.target.value)}
                                        className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent"
                                    />
                                    <input
                                        type="text"
                                        value={editLabel}
                                        onChange={e => setEditLabel(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleSaveEdit()}
                                        className="flex-1 bg-[var(--input-bg)] border border-[var(--input-border)] rounded px-2 py-1 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--primary-button)]"
                                        autoFocus
                                    />
                                    <button
                                        onClick={handleSaveEdit}
                                        className="text-[var(--primary-button)] hover:opacity-80 transition-opacity"
                                        title="Save"
                                    >
                                        <span className="material-symbols-outlined text-sm">check</span>
                                    </button>
                                    <button
                                        onClick={() => setEditingId(null)}
                                        className="text-[var(--text-muted)] hover:opacity-80 transition-opacity"
                                        title="Cancel"
                                    >
                                        <span className="material-symbols-outlined text-sm">close</span>
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div
                                        className="w-5 h-5 rounded-full flex-shrink-0"
                                        style={{ backgroundColor: bt.color }}
                                    />
                                    <span className="flex-1 text-sm text-[var(--text-primary)]">{bt.label}</span>
                                    <button
                                        onClick={() => handleStartEdit(bt)}
                                        className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                                        title="Edit"
                                    >
                                        <span className="material-symbols-outlined text-sm">edit</span>
                                    </button>
                                    <button
                                        onClick={() => handleDelete(bt.id)}
                                        className="text-[var(--text-muted)] hover:text-[var(--error)] transition-colors"
                                        title="Delete"
                                    >
                                        <span className="material-symbols-outlined text-sm">delete</span>
                                    </button>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="border-t border-[var(--surface-border)] pt-4">
                <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wide mb-3">
                    Templates
                </h3>
                {templates.length === 0 ? (
                    <p className="text-sm text-[var(--text-muted)]">No saved templates yet.</p>
                ) : (
                    <div className="space-y-2">
                        {templates.map(t => (
                            <div key={t.id} className="flex items-center gap-3 py-2 px-3 rounded-lg bg-[var(--surface-bg-lighter)]">
                                {tplRenamingId === t.id ? (
                                    <>
                                        <input
                                            type="text"
                                            value={tplRenameValue}
                                            onChange={e => setTplRenameValue(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') handleTplRenameSave(); if (e.key === 'Escape') setTplRenamingId(null); }}
                                            className="flex-1 bg-[var(--input-bg)] border border-[var(--input-border)] rounded px-2 py-1 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--primary-button)]"
                                            autoFocus
                                        />
                                        <button onClick={handleTplRenameSave} className="text-[var(--primary-button)] hover:opacity-80 transition-opacity" title="Save">
                                            <span className="material-symbols-outlined text-sm">check</span>
                                        </button>
                                        <button onClick={() => setTplRenamingId(null)} className="text-[var(--text-muted)] hover:opacity-80 transition-opacity" title="Cancel">
                                            <span className="material-symbols-outlined text-sm">close</span>
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex-1 min-w-0">
                                            <span className="text-sm text-[var(--text-primary)] block truncate">{t.name}</span>
                                            <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                                                {t.blocks.map((b, i) => {
                                                    const bt = getBlockType(b.blockTypeId);
                                                    return (
                                                        <span key={i} className="inline-flex items-center gap-1 text-xs text-[var(--text-muted)]">
                                                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: bt.color }} />
                                                            {bt.label}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => { handleTplRenameStart(t); }}
                                            className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                                            title="Rename"
                                        >
                                            <span className="material-symbols-outlined text-sm">edit</span>
                                        </button>
                                        {tplDeleteConfirmId === t.id ? (
                                            <div className="flex -space-x-2">
                                                <button onClick={() => handleTplDelete(t.id)} className="px-1 py-1 text-[var(--error)] hover:text-[var(--error-hover)]">
                                                    <span className="material-symbols-outlined text-sm">check</span>
                                                </button>
                                                <button onClick={() => setTplDeleteConfirmId(null)} className="px-1 py-1 text-[var(--text-muted)] hover:text-[var(--text-secondary)]">
                                                    <span className="material-symbols-outlined text-sm">close</span>
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => { setTplDeleteConfirmId(t.id); setTplRenamingId(null); }}
                                                className="text-[var(--text-muted)] hover:text-[var(--error)] transition-colors"
                                                title="Delete"
                                            >
                                                <span className="material-symbols-outlined text-sm">delete</span>
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="border-t border-[var(--surface-border)] pt-4">
                <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wide mb-3">
                    Add Block Type
                </h3>
                <div className="flex items-center gap-3">
                    <input
                        type="color"
                        value={newColor}
                        onChange={e => setNewColor(e.target.value)}
                        className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent flex-shrink-0"
                        title="Pick color"
                    />
                    <input
                        type="text"
                        placeholder="Label (e.g. Planning)"
                        value={newLabel}
                        onChange={e => setNewLabel(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleAdd()}
                        className="flex-1 bg-[var(--input-bg)] border border-[var(--input-border)] rounded px-3 py-1.5 text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--primary-button)]"
                    />
                    <button
                        onClick={handleAdd}
                        disabled={!newLabel.trim()}
                        className="px-3 py-1.5 bg-[var(--primary-button)] text-white rounded text-sm font-medium hover:bg-[var(--primary-button-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        Add
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TimeBlockSettings;
